import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faAirConditioner,
  faBolt,
  faCar,
  faCouch,
  faDroplet,
  faDryer,
  faGear,
  faHotTub,
  faOven,
  faSignalStream,
  faToolbox,
  faTruck,
  faWrench,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HousePlaceholderImg from '~/images/images/HousePlaceholder.webp';

/**
 * Mapping of asset types to their placeholder images/icons
 * Used throughout the app for consistent asset type representation
 */
export const ASSET_TYPE_IMAGES: Record<string, React.ReactNode | string> = {
  // Appliances - use dryer icon (laundry-related)
  Appliances: (
    <FontAwesomeIcon icon={faOven as IconProp} className='h-12 w-12' />
  ),

  // Cleaning equipment - use wrench/tool icon
  'Cleaning equipment': (
    <FontAwesomeIcon icon={faWrench as IconProp} className='h-12 w-12' />
  ),

  // Compressors / Generators - use bolt icon (power/energy)
  'Compressors / Generators': (
    <FontAwesomeIcon icon={faBolt as IconProp} className='h-12 w-12' />
  ),
  Compressors: (
    <FontAwesomeIcon icon={faBolt as IconProp} className='h-12 w-12' />
  ),
  Generators: (
    <FontAwesomeIcon icon={faBolt as IconProp} className='h-12 w-12' />
  ),

  // Equipment - use gear icon
  Equipment: (
    <FontAwesomeIcon icon={faGear as IconProp} className='h-12 w-12' />
  ),

  // Furniture - use couch icon (perfect representation of furniture)
  Furniture: (
    <FontAwesomeIcon icon={faCouch as IconProp} className='h-12 w-12' />
  ),

  // HVAC units - use air conditioner icon
  'HVAC units': (
    <FontAwesomeIcon
      icon={faAirConditioner as IconProp}
      className='h-12 w-12'
    />
  ),
  HVAC: (
    <FontAwesomeIcon
      icon={faAirConditioner as IconProp}
      className='h-12 w-12'
    />
  ),
  'Air Conditioner': (
    <FontAwesomeIcon
      icon={faAirConditioner as IconProp}
      className='h-12 w-12'
    />
  ),
  'Air conditioner': (
    <FontAwesomeIcon
      icon={faAirConditioner as IconProp}
      className='h-12 w-12'
    />
  ),
  'Air Conditioning Units': (
    <FontAwesomeIcon
      icon={faAirConditioner as IconProp}
      className='h-12 w-12'
    />
  ),
  'Air Conditioning units': (
    <FontAwesomeIcon
      icon={faAirConditioner as IconProp}
      className='h-12 w-12'
    />
  ),
  'air conditioning units': (
    <FontAwesomeIcon
      icon={faAirConditioner as IconProp}
      className='h-12 w-12'
    />
  ),

  // Heavy equipment - use truck icon
  'Heavy equipment': (
    <FontAwesomeIcon icon={faTruck as IconProp} className='h-12 w-12' />
  ),

  // Hot water systems - use droplet icon (water-related)
  'Hot water systems': (
    <FontAwesomeIcon icon={faDroplet as IconProp} className='h-12 w-12' />
  ),
  'Hot Water Systems': (
    <FontAwesomeIcon icon={faDroplet as IconProp} className='h-12 w-12' />
  ),

  // Industrial pumps - use droplet icon (water/pump-related)
  'Industrial pumps': (
    <FontAwesomeIcon icon={faDroplet as IconProp} className='h-12 w-12' />
  ),
  Pumps: <FontAwesomeIcon icon={faDroplet as IconProp} className='h-12 w-12' />,

  // Laundry machines - use dryer icon
  'Laundry machines': (
    <FontAwesomeIcon icon={faDryer as IconProp} className='h-12 w-12' />
  ),
  'Laundry Machines': (
    <FontAwesomeIcon icon={faDryer as IconProp} className='h-12 w-12' />
  ),

  // Machinery - use gear icon
  Machinery: (
    <FontAwesomeIcon icon={faGear as IconProp} className='h-12 w-12' />
  ),

  // Outdoor equipment - use toolbox icon
  'Outdoor equipment': (
    <FontAwesomeIcon icon={faToolbox as IconProp} className='h-12 w-12' />
  ),
  'Outdoor Equipment': (
    <FontAwesomeIcon icon={faToolbox as IconProp} className='h-12 w-12' />
  ),

  // Plumbing equipment - use droplet icon
  'Plumbing equipment': (
    <FontAwesomeIcon icon={faDroplet as IconProp} className='h-12 w-12' />
  ),
  'Plumbing Equipment': (
    <FontAwesomeIcon icon={faDroplet as IconProp} className='h-12 w-12' />
  ),

  // Plumbing fixtures & pumps - use droplet icon
  'Plumbing fixtures & pumps': (
    <FontAwesomeIcon icon={faDroplet as IconProp} className='h-12 w-12' />
  ),
  'Plumbing Fixtures & Pumps': (
    <FontAwesomeIcon icon={faDroplet as IconProp} className='h-12 w-12' />
  ),

  // Pools & Spas - use hot tub icon
  'Pools & Spas': (
    <FontAwesomeIcon icon={faHotTub as IconProp} className='h-12 w-12' />
  ),
  'Pools Spas': (
    <FontAwesomeIcon icon={faHotTub as IconProp} className='h-12 w-12' />
  ),
  'pools & spas': (
    <FontAwesomeIcon icon={faHotTub as IconProp} className='h-12 w-12' />
  ),
  'pools spas': (
    <FontAwesomeIcon icon={faHotTub as IconProp} className='h-12 w-12' />
  ),

  // Power tools - use bolt icon
  'Power tools': (
    <FontAwesomeIcon icon={faBolt as IconProp} className='h-12 w-12' />
  ),
  'Power Tools': (
    <FontAwesomeIcon icon={faBolt as IconProp} className='h-12 w-12' />
  ),

  // Tools & Equipment - use toolbox icon
  'Tools & Equipment': (
    <FontAwesomeIcon icon={faToolbox as IconProp} className='h-12 w-12' />
  ),
  'Tools & equipment': (
    <FontAwesomeIcon icon={faToolbox as IconProp} className='h-12 w-12' />
  ),

  // Trailers - use truck icon
  Trailers: (
    <FontAwesomeIcon icon={faTruck as IconProp} className='h-12 w-12' />
  ),

  // Vehicles - use car icon
  Vehicles: <FontAwesomeIcon icon={faCar as IconProp} className='h-12 w-12' />,

  // Welders - use bolt icon (power/energy-related)
  Welders: <FontAwesomeIcon icon={faBolt as IconProp} className='h-12 w-12' />,

  // Smoke alarm - use signal stream icon
  'Smoke alarm': (
    <FontAwesomeIcon icon={faSignalStream as IconProp} className='h-12 w-12' />
  ),
  'Smoke Alarm': (
    <FontAwesomeIcon icon={faSignalStream as IconProp} className='h-12 w-12' />
  ),

  // Generic fallback - use house placeholder image
  Property: HousePlaceholderImg,

  // Default fallback - use generic cube icon
  default: <FontAwesomeIcon icon={faDryer as IconProp} className='h-12 w-12' />,
};

/**
 * Get the placeholder image/icon for an asset type
 * @param assetType - The asset type string
 * @returns React node (icon) or string (image path), or default icon if not found
 */
export const getAssetTypePlaceholder = (
  assetType: string | null | undefined
): React.ReactNode | string => {
  if (!assetType) {
    return ASSET_TYPE_IMAGES.default;
  }

  // Try exact match first
  if (ASSET_TYPE_IMAGES[assetType]) {
    return ASSET_TYPE_IMAGES[assetType];
  }

  // Try case-insensitive match
  const normalizedType = assetType.trim();
  const caseInsensitiveMatch = Object.keys(ASSET_TYPE_IMAGES).find(
    (key) => key.toLowerCase() === normalizedType.toLowerCase()
  );

  if (caseInsensitiveMatch) {
    return ASSET_TYPE_IMAGES[caseInsensitiveMatch];
  }

  // Return default fallback
  return ASSET_TYPE_IMAGES.default;
};

/**
 * Render the placeholder component for an asset type
 * Handles both React nodes (icons) and image paths
 */
export const renderAssetTypePlaceholder = (
  assetType: string | null | undefined
): React.ReactNode => {
  const placeholder = getAssetTypePlaceholder(assetType);

  // If it's a string, it's an image path
  if (typeof placeholder === 'string') {
    return (
      <div className='flex h-full w-full items-center justify-center overflow-hidden rounded bg-shade-10 text-shade-50'>
        <img
          src={placeholder}
          alt={`${assetType || 'Asset'} placeholder`}
          className='h-full w-full object-cover'
        />
      </div>
    );
  }

  // Otherwise it's a React node (icon)
  return (
    <div className='flex h-full w-full items-center justify-center rounded bg-shade-10 text-shade-50'>
      {placeholder}
    </div>
  );
};
